'use client';

import{ useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { IBirthdayBanner, IBirthdayBannerTier } from '@/sanity/lib/definitions';
import { DEFAULT_LANGUAGE, resolveI18nText } from '@/sanity/globals/I18n';
import { cn } from '@/lib/utils';
import { resolveLink } from '@/sanity/globals/CTA';
import styles from '@/scss/global/BirthdayTopBanner.module.scss';
import assetLeft from '@/public/global/birthday/birthday-asset-left.png';
import assetRight from '@/public/global/birthday/birthday-asset-right.png';

interface BirthdayTopBannerProps {
  data: IBirthdayBanner;
  ctaUrl: string;
  language?: string;
}

interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

function getTimeLeft(endDate: Date): TimeLeft {
  const diff = endDate.getTime() - Date.now();
  if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0, expired: true };
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { hours, minutes, seconds, expired: false };
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function parseHighlightedText(text: string) {
  const parts = text.split(/\[\[(.+?)\]\]/g);
  return (
    <div className="flex items-center gap-[0.5rem] md:gap-[0.75rem] flex-wrap justify-start md:justify-center">
      {parts.map((part, i) => {
        if (!part.trim()) return null;
        if (i % 2 === 1) {
          return (
            <span
              key={i}
              className="inline-flex flex-col items-center shrink-0 relative"
            >
              <span
                className="font-blacker italic text-[2.25rem] md:text-[4.5rem] leading-[1.12] tracking-[-0.1rem] md:tracking-[-0.14rem] relative z-10"
                style={{ color: '#d6eee7' }}
              >
                {part}
              </span>
              <span
                className="w-full h-[1.25rem] md:h-[2.375rem] absolute block bottom-[0rem] left-0"
                style={{ background: 'rgba(34,142,113,0.13)' }}
              />
            </span>
          );
        }
        return (
          <span
            key={i}
            className="font-geist font-semibold text-[1.5rem] md:text-[2.75rem] leading-[0.95] tracking-[-0.08rem] md:tracking-[-0.14rem] whitespace-nowrap"
            style={{ color: '#fefefe' }}
          >
            {part.trim()}
          </span>
        );
      })}
    </div>
  );
}

export default function BirthdayTopBanner({
  data,
  ctaUrl,
  language,
}: BirthdayTopBannerProps) {
  const endDate = useMemo(
    () => (data.countdownEndDate ? new Date(data.countdownEndDate) : null),
    [data.countdownEndDate],
  );
  const link = resolveLink(ctaUrl, language || DEFAULT_LANGUAGE);
  const [finalLink, setFinalLink] = useState("");
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  useEffect(() => {
    if (!endDate) return;
    setTimeLeft(getTimeLeft(endDate));
    const interval = setInterval(() => setTimeLeft(getTimeLeft(endDate)), 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  if (endDate && timeLeft?.expired) return null;

  const mainText = resolveI18nText(
    data.headerMainText,
    data.headerMainTextI18n,
    language,
  );
  const ctaLabel = resolveI18nText(
    data.headerCtaLabel,
    data.headerCtaLabelI18n,
    language,
  );
  const sectionLabel = resolveI18nText(
    data.sectionLabel,
    data.sectionLabelI18n,
    language,
  );
  const endsInLabel = resolveI18nText(
    data.endsInLabel ?? 'ENDS IN',
    data.endsInLabelI18n,
    language,
  );
  const hoursLabel = resolveI18nText(
    data.hoursLabel ?? 'HRS',
    data.hoursLabelI18n,
    language,
  );
  const minutesLabel = resolveI18nText(
    data.minutesLabel ?? 'MIN',
    data.minutesLabelI18n,
    language,
  );
  const secondsLabel = resolveI18nText(
    data.secondsLabel ?? 'SEC',
    data.secondsLabelI18n,
    language,
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 'calc(-100% - 1.5rem)' }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="w-full relative overflow-hidden"
      style={{ background: '#071d14' }}
    >
      {/* Confetti decoration */}

      <div className="absolute lg:block hidden inset-0 z-0 pointer-events-none">
        <div className="absolute top-[0rem] left-0 w-[30%] h-full">
          <Image
            className="!w-full !h-full !object-contain"
            src={assetLeft}
            alt="Birthday Asset Left"
          />
        </div>
        <div className="absolute top-[0rem] right-0 w-[30%] h-full">
          <Image
            className="!w-full !h-full !object-contain"
            src={assetRight}
            alt="Birthday Asset Right"
          />
        </div>
      </div>
      {/* Row 1 — main content */}
      <div className="relative z-10 flex flex-row items-center justify-between md:justify-center gap-[3rem] md:gap-[5rem] px-[1.5rem] md:px-[4rem] py-[1.25rem] md:py-[1.5rem] min-h-[5rem] md:min-h-[8rem]">
        {/* Main text */}
        <div className="lg:w-fit w-[60%]">
          {parseHighlightedText(mainText ?? '')}
        </div>

        {/* Countdown */}
        {endDate && timeLeft && (
          <div className="hidden md:flex flex-col items-center gap-[1rem] shrink-0 w-[20rem]">
            <span
              className="font-mono text-[1.5rem] font-normal tracking-[-0.075rem] text-center"
              style={{ color: '#1aa87f' }}
            >
              {endsInLabel}
            </span>
            <div className="flex items-center gap-[0.625rem] w-full">
              <CountdownBox value={pad(timeLeft.hours)} />
              <span
                className="font-semibold text-[1.5rem] leading-[0.95] tracking-[-0.075rem] shrink-0"
                style={{ color: '#bfe2d8' }}
              >
                :
              </span>
              <CountdownBox value={pad(timeLeft.minutes)} />
              <span
                className="font-semibold text-[1.5rem] leading-[0.95] tracking-[-0.075rem] shrink-0"
                style={{ color: '#bfe2d8' }}
              >
                :
              </span>
              <CountdownBox value={pad(timeLeft.seconds)} />
            </div>
            <div className="flex items-center justify-between w-full px-[0.25rem]">
              <span
                className="font-mono text-[1.25rem] font-normal tracking-[-0.065rem] text-center flex-1"
                style={{ color: '#7a7a7a' }}
              >
                {hoursLabel}
              </span>
              <span
                className="font-mono text-[1.25rem] font-normal tracking-[-0.065rem] text-center flex-1"
                style={{ color: '#7a7a7a' }}
              >
                {minutesLabel}
              </span>
              <span
                className="font-mono text-[1.25rem] font-normal tracking-[-0.065rem] text-center flex-1"
                style={{ color: '#7a7a7a' }}
              >
                {secondsLabel}
              </span>
            </div>
          </div>
        )}

        {/* CTA */}
        <Link
          href={finalLink ?? ctaUrl}
          className="shrink-0 flex items-center justify-center gap-[0.375rem] px-[1.25rem] md:px-[2.5rem] py-[1rem] md:py-[1.375rem] rounded-[0.25rem] font-mono text-[1.25rem] md:text-[1.5rem] font-semibold text-white tracking-[0.02em] uppercase whitespace-nowrap hover:brightness-110 transition-all"
          style={{
            background: 'linear-gradient(to bottom, #2ea07a 0%, #228e71 100%)',
            boxShadow: 'inset 1px 0px 3px rgba(255,255,255,0.45)',
          }}
        >
          {ctaLabel}
          <svg
            className="shrink-0"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="white"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M10.7833 8.66667H2.66667V7.33333H10.7833L7.05 3.6L8 2.66667L13.3333 8L8 13.3333L7.05 12.4L10.7833 8.66667Z" />
          </svg>
        </Link>
      </div>

      {/* Row 2 — tier bar */}
      {data.showTierBar !== false &&
        (sectionLabel || (data.tiers && data.tiers.length > 0)) && (
          <div
            className="relative z-10 py-[1.25rem] md:py-[1.75rem]"
            style={{
              background: '#00110c',
              borderTop: '0.5px solid rgba(219,219,219,0.1)',
            }}
          >
            {/* Mobile: label above, chips in grid */}
            <div className="flex flex-col gap-[1rem] px-[1.5rem] md:hidden">
              {sectionLabel && (
                <span className="text-[#fefefe] font-semibold text-[1.625rem] tracking-[-0.1rem] leading-[0.95] text-center w-full">
                  {sectionLabel}
                </span>
              )}
              <div className="grid grid-cols-2 gap-[0.5rem]">
                {data.tiers?.map((tier, i) => (
                  <TierChip key={i} tier={tier} language={language} mobile />
                ))}
              </div>
            </div>

            {/* Desktop: inline row */}
            <div className="hidden md:flex items-center gap-[2.38rem] justify-center">
              {sectionLabel && (
                <span className="text-[#fefefe] font-semibold text-[2rem] tracking-[-0.1rem] leading-[0.95] whitespace-nowrap shrink-0">
                  {sectionLabel}
                </span>
              )}
              <div className="flex items-stretch gap-[0.625rem] w-[68rem]">
                {data.tiers?.map((tier, i) => (
                  <TierChip key={i} tier={tier} language={language} />
                ))}
              </div>
            </div>
          </div>
        )}
    </motion.div>
  );
}

function CountdownBox({ value }: { value: string }) {
  return (
    <div
      className={cn(
        'flex-1 flex items-center justify-center overflow-hidden py-[0.75rem] min-w-0',
        styles.countdownBox,
      )}
      style={{
        background:
          'linear-gradient(to bottom, rgba(0,182,122,0), rgba(0,182,122,0.1))',
      }}
    >
      <span
        className="font-semibold tabular-nums text-[2.125rem] leading-[0.95] tracking-[-0.1rem] text-center w-full"
        style={{ color: '#bfe2d8' }}
      >
        {value}
      </span>
    </div>
  );
}

function TierChip({
  tier,
  language,
  mobile,
}: {
  tier: IBirthdayBannerTier;
  language?: string;
  mobile?: boolean;
}) {
  const label = resolveI18nText(tier.label, tier.labelI18n, language);
  const isHighlighted = !!tier.highlighted;

  return (
    <div
      className={cn(
        'relative flex-1 flex flex-col items-center justify-center overflow-hidden min-w-[16rem]',
        mobile ? 'p-[0.875rem] min-w-0 w-full' : 'p-[1.25rem]',
        styles.tierChip,
      )}
      style={{
        background: isHighlighted
          ? 'linear-gradient(to bottom, rgba(0,182,122,0), rgba(0,182,122,0.6))'
          : 'linear-gradient(to bottom, rgba(0,182,122,0), rgba(0,182,122,0.1))',
      }}
    >
      <div className="flex flex-col w-full gap-[0.625rem] items-center text-center">
        <span
          className={cn(
            'font-mono font-normal tracking-[-0.065rem] leading-[1.5] w-full',
            mobile ? 'text-[1rem]' : 'text-[1.25rem]',
          )}
          style={{ color: isHighlighted ? '#d0bd81' : '#1aa87f' }}
        >
          {label}
        </span>
        <span
          className={cn(
            'font-semibold tracking-[-0.1rem] leading-[0.95] w-full',
            mobile ? 'text-[1.75rem]' : 'text-[2.125rem]',
          )}
          style={{ color: '#bfe2d8' }}
        >
          {tier.discount}
        </span>
      </div>
    </div>
  );
}
